


import React, { useState, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  Typography, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, TextField, InputAdornment, Tabs, Tab, Stack, Card, CardContent, CardActions,
  Grid, ToggleButtonGroup, ToggleButton, Tooltip, Divider, Badge
} from '@mui/material';
import {
  LocalShipping, Close as CloseIcon, CheckCircle, Search, Refresh,
  GridView as GridViewIcon, ViewList as ViewListIcon, Person, Phone,
  CalendarToday, Description, MedicalServices, Inventory2
} from '@mui/icons-material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

import { orderAssignmentAPI } from "./deliveryAgentAPI";
import CustomDialog from "../../CustomDialog";
import { fetchUserFromRedis } from "../AuthonticationPages/Register";

// ─── Package Bookings API ────────────────────────────────────────────────────
const packageBookingAPI = {
  // Fetch confirmed package bookings (status = "Confirmed")
  getConfirmedPackageBookings: () =>
    fetch(`${process.env.REACT_APP_API}/package-orders/admin/all`, {
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    }).then(r => r.json()),

  // Assign sample collection agent to a package booking
  assignPackageAgent: (bookingId, agentId) =>
    fetch(`${process.env.REACT_APP_API}/package-orders/admin/assign-agent`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ bookingId, agentId }),
    }).then(r => r.json()),
};

// ─── Main Component ──────────────────────────────────────────────────────────
const DeliveryOrdersManagement = () => {
  // ── Diagnostic / Medicine state ───────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // diagnosticadmin starts on 'booking'; medicineadmin on 'medicine'; admin on 'all'
  const initialFilterTab = (() => {
    const role = sessionStorage.getItem("userrole");
    if (role === 'diagnosticadmin') return 'booking';
    if (role === 'medicineadmin')   return 'medicine';
    return 'all';
  })();
  const [filterTab, setFilterTab] = useState(initialFilterTab);
  const [viewMode, setViewMode] = useState('cards');
  const [user, setUser] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');

  // ── Package Bookings state ────────────────────────────────────────────────
  // mainTab removed — filterTab now drives both order filters AND packages tab
  const [packageBookings, setPackageBookings] = useState([]);
  const [filteredPackageBookings, setFilteredPackageBookings] = useState([]);
  const [packageSearchQuery, setPackageSearchQuery] = useState('');
  const [packageLoading, setPackageLoading] = useState(false);
  const [packageAgentSearchQuery, setPackageAgentSearchQuery] = useState('');
  const [packageAssignLoading, setPackageAssignLoading] = useState(false);
  const [packageViewMode, setPackageViewMode] = useState('cards');

  const [packageAssignDialog, setPackageAssignDialog] = useState({ open: false, booking: null });
  const [packageConfirmDialog, setPackageConfirmDialog] = useState({ open: false, agent: null, booking: null });

  const userRole = user?.role || sessionStorage.getItem("userrole");

  // ── Shared dialog state ───────────────────────────────────────────────────
  const [assignDialog, setAssignDialog] = useState({ open: false, order: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, agent: null, order: null });
  const [notificationDialog, setNotificationDialog] = useState({
    open: false, type: 'success', title: '', message: ''
  });

  // ── Load user ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadUser = async () => {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      if (!userId || !token) return;
      try {
        const data = await fetchUserFromRedis(userId, token);
        setUser(data || null);
        if (data?.role) sessionStorage.setItem("userrole", data.role);
      } catch (error) {
        console.error("Failed to load user:", error);
      }
    };
    loadUser();
  }, []);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { filterOrders(); }, [orders, searchQuery, filterTab, userRole]);
  useEffect(() => { filterPackageBookings(); }, [packageBookings, packageSearchQuery]);

  // ── Fetch diagnostic + agent data ─────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const [ordersRes, agentsRes] = await Promise.all([
        orderAssignmentAPI.getReadyForDelivery(),
        orderAssignmentAPI.getActiveAgents(),
      ]);
      if (ordersRes.data.success) setOrders(ordersRes.data.data);
      if (agentsRes.data.success) setAgents(agentsRes.data.data);
    } catch (error) {
      setNotificationDialog({
        open: true, type: 'error',
        title: 'Error Loading Data',
        message: error.response?.data?.message || error.message || 'Failed to load data.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch package bookings ────────────────────────────────────────────────
  const fetchPackageBookings = async () => {
    try {
      setPackageLoading(true);
      const res = await packageBookingAPI.getConfirmedPackageBookings();
      if (res.success) {
        // Only show "Confirmed" bookings that don't already have an agent assigned
        const confirmed = (res.bookings || []).filter(b => b.status === 'Confirmed');
        setPackageBookings(confirmed);
      }
    } catch (error) {
      setNotificationDialog({
        open: true, type: 'error',
        title: 'Error Loading Package Bookings',
        message: error.message || 'Failed to load package bookings.',
      });
    } finally {
      setPackageLoading(false);
    }
  };

  // Load package bookings when Packages tab becomes active
  useEffect(() => {
    const role = user?.role || sessionStorage.getItem("userrole");
    const canLoad = role === 'admin' || role === 'diagnosticadmin';
    if (canLoad && filterTab === 'packages' && packageBookings.length === 0) {
      fetchPackageBookings();
    }
  }, [filterTab, user]);

  // Pre-fetch on mount so the badge count shows immediately
  useEffect(() => {
    const role = user?.role || sessionStorage.getItem("userrole");
    if ((role === 'diagnosticadmin' || role === 'admin') && packageBookings.length === 0) {
      fetchPackageBookings();
    }
  }, [user]);

  // ── Filter helpers ────────────────────────────────────────────────────────
  const filterOrders = () => {
    let filtered = [...orders];
    // role-based base filter
    if (userRole === 'medicineadmin') {
      filtered = filtered.filter(o => o.orderType === 'medicine');
    } else if (userRole === 'diagnosticadmin') {
      filtered = filtered.filter(o => o.orderType === 'booking');
    }
    // admin sub-filter tab (skip when 'packages' is selected)
    if (userRole === 'admin' && filterTab !== 'all' && filterTab !== 'packages') {
      filtered = filtered.filter(o => o.orderType === filterTab);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(o =>
        o.orderId?.toLowerCase().includes(q) ||
        o.customerName?.toLowerCase().includes(q) ||
        o.customerPhone?.includes(q) ||
        o.serviceName?.toLowerCase().includes(q)
      );
    }
    setFilteredOrders(filtered);
  };

  const filterPackageBookings = () => {
    if (!packageSearchQuery.trim()) {
      setFilteredPackageBookings(packageBookings);
      return;
    }
    const q = packageSearchQuery.toLowerCase();
    setFilteredPackageBookings(
      packageBookings.filter(b =>
        b.booking_ref?.toLowerCase().includes(q) ||
        b.patient_name?.toLowerCase().includes(q) ||
        b.phone?.includes(q) ||
        b.package_name?.toLowerCase().includes(q)
      )
    );
  };

  const getSampleAgents = () => agents.filter(a => a.agentRole === 'sample_collection');

  const getSearchFilteredAgents = (order) => {
    let filtered = getFilteredAgents(order);
    if (agentSearchQuery.trim()) {
      const q = agentSearchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.name?.toLowerCase().includes(q) ||
        a.id?.toString().includes(q) ||
        a.phone?.includes(q)
      );
    }
    return filtered;
  };

  const getSearchFilteredPackageAgents = () => {
    let filtered = getSampleAgents();
    if (packageAgentSearchQuery.trim()) {
      const q = packageAgentSearchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.name?.toLowerCase().includes(q) ||
        a.id?.toString().includes(q) ||
        a.phone?.includes(q)
      );
    }
    return filtered;
  };

  // ── Diagnostic assignment handlers ────────────────────────────────────────
  const handleConfirmAssignment = async () => {
    try {
      setAssignLoading(true);
      const { agent, order } = confirmDialog;
      let deliveryType = 'sample';
      if (order.orderType === 'medicine') deliveryType = 'medicine';
      else if (order.deliveryType === 'report' || order.orderStatus === 'report-dispatched') deliveryType = 'report';

      const response = await orderAssignmentAPI.assignAgent(order.id, order.orderType, agent.id, deliveryType);
      if (response.data.success) {
        setNotificationDialog({
          open: true, type: 'success', title: 'Success',
          message: `Agent ${agent.name} assigned to order ${order.orderId}.`,
        });
        setConfirmDialog({ open: false, agent: null, order: null });
        setAssignDialog({ open: false, order: null });
        fetchData();
      } else throw new Error(response.data.message || 'Failed to assign agent');
    } catch (error) {
      setNotificationDialog({
        open: true, type: 'error', title: 'Assignment Failed',
        message: error.response?.data?.message || error.message || 'Failed to assign agent.',
      });
      setConfirmDialog({ open: false, agent: null, order: null });
    } finally {
      setAssignLoading(false);
    }
  };

  // ── Package assignment handlers ───────────────────────────────────────────
  const handlePackageConfirmAssignment = async () => {
    try {
      setPackageAssignLoading(true);
      const { agent, booking } = packageConfirmDialog;
      const res = await packageBookingAPI.assignPackageAgent(booking.id, agent.id);
      if (res.success) {
        setNotificationDialog({
          open: true, type: 'success', title: 'Success',
          message: `Agent ${agent.name} assigned to package booking ${booking.booking_ref}.`,
        });
        setPackageConfirmDialog({ open: false, agent: null, booking: null });
        setPackageAssignDialog({ open: false, booking: null });
        fetchPackageBookings();
      } else throw new Error(res.message || 'Failed to assign agent');
    } catch (error) {
      setNotificationDialog({
        open: true, type: 'error', title: 'Assignment Failed',
        message: error.message || 'Failed to assign agent.',
      });
      setPackageConfirmDialog({ open: false, agent: null, booking: null });
    } finally {
      setPackageAssignLoading(false);
    }
  };

  // ── Status / colour helpers ───────────────────────────────────────────────
  const getStatusColor = (status) => ({
    pending: 'warning', packed: 'info', confirmed: 'primary', dispatched: 'secondary',
    delivered: 'success', cancelled: 'error', Confirmed: 'primary',
    'sample-collected': 'info', 'test-in-progress': 'warning',
    'report-ready': 'success', 'report-dispatched': 'info', 'report-delivered': 'success',
    Pending: 'warning', Completed: 'success', Cancelled: 'error',
  }[status] || 'default');

  const getOrderTypeColor = (type) => type === 'medicine' ? 'secondary' : 'primary';

  const getAssignmentStatusColor = (order) => {
    if (isOrderAssigned(order)) {
      return (order.orderStatus === 'dispatched' || order.orderStatus === 'report-dispatched') ? 'success' : 'info';
    }
    return 'warning';
  };

  const getAssignmentStatusLabel = (order) => {
    if (order.orderType === 'booking' && order.deliveryType === 'report')
      return order.reportDeliveryAgent ? 'Report Agent Assigned' : 'Not Assigned';
    if (order.deliveryAgent)
      return order.orderType === 'medicine' && order.orderStatus === 'dispatched' ? 'Dispatched' : 'Agent Assigned';
    return 'Not Assigned';
  };

  const isOrderAssigned = (order) => {
    if (order.orderType === 'booking' && order.deliveryType === 'report') return !!order.reportDeliveryAgent;
    return !!order.deliveryAgent;
  };

  const getFilteredAgents = (order) => {
    if (order.orderType === 'medicine') return agents.filter(a => a.agentRole === 'delivery');
    if (order.orderType === 'booking') {
      if (order.deliveryType === 'report' || order.orderStatus === 'report-dispatched')
        return agents.filter(a => a.agentRole === 'delivery');
      return agents.filter(a => a.agentRole === 'sample_collection');
    }
    return agents;
  };

  const getAssignmentTypeLabel = (order) => {
    if (order.orderType === 'medicine') return 'Delivery';
    if (order.deliveryType === 'report' || order.orderStatus === 'report-dispatched') return 'Report Delivery';
    return 'Sample Collection';
  };

  const isPackageAssigned = (booking) => !!booking.assigned_agent_id || !!booking.agent_name;

  // ─────────────────────────────────────────────────────────────────────────
  // ── Agent Selector (reusable inside dialogs) ─────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  const AgentList = ({ agents: agentList, onSelect }) => (
    agentList.length === 0
      ? (
        <Paper sx={{ p: 3, bgcolor: '#fff3e0', border: '1px solid #ff9800', mt: 2 }}>
          <Typography color="text.secondary">No available sample collection agents.</Typography>
        </Paper>
      ) : (
        <Box sx={{ mt: 2 }}>
          {agentList.map(agent => (
            <Paper
              key={agent.id}
              sx={{
                p: 2, mb: 2, cursor: 'pointer', border: '2px solid transparent',
                '&:hover': { borderColor: '#006D77', bgcolor: '#f5f5f5' },
              }}
              onClick={() => onSelect(agent)}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body1" fontWeight={500}>{agent.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {agent.phone} • {agent.vehicleType?.toUpperCase() || 'N/A'}
                  </Typography>
                  <Box mt={1}>
                    <Chip
                      label={agent.agentRole?.replace('_', ' ').toUpperCase()}
                      size="small" color="primary" sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${agent.currentOrders || 0}/${agent.maxOrderCapacity || 5} Orders`}
                      size="small" variant="outlined"
                      color={agent.availableCapacity > 2 ? 'success' : 'warning'}
                    />
                  </Box>
                </Box>
                <CheckCircle sx={{ color: '#006D77', fontSize: 28 }} />
              </Box>
            </Paper>
          ))}
        </Box>
      )
  );

  // ─────────────────────────────────────────────────────────────────────────
  // ── Package Booking Card ──────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  const PackageCard = ({ booking }) => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{
        height: '100%', display: 'flex', flexDirection: 'column',
        transition: 'all 0.3s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
      }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={600}>{booking.booking_ref}</Typography>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <CalendarToday sx={{ fontSize: 14 }} />
                {booking.preferred_date} · {booking.preferred_time}
              </Typography>
            </Box>
            <Chip label="PACKAGE" size="small" color="secondary" sx={{ fontWeight: 500 }} />
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box mb={1.5}>
            <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5} mb={0.5}>
              <Inventory2 sx={{ fontSize: 16 }} /> Package
            </Typography>
            <Typography variant="body1" fontWeight={500}>{booking.package_name}</Typography>
          </Box>

          <Box mb={1.5}>
            <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5} mb={0.5}>
              <Person sx={{ fontSize: 16 }} /> Patient
            </Typography>
            <Typography variant="body1" fontWeight={500}>{booking.patient_name}</Typography>
            <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
              <Phone sx={{ fontSize: 14 }} /> {booking.phone}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>Status</Typography>
              <Chip label={booking.status} size="small" color={getStatusColor(booking.status)} />
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" color="text.secondary" gutterBottom>Amount</Typography>
              <Typography variant="h6" fontWeight={600} color="primary" display="flex" alignItems="center" gap={0.5}>
                <CurrencyRupeeIcon sx={{ fontSize: 18 }} />
                {booking.offer_price?.toFixed(2) || '0.00'}
              </Typography>
            </Box>
          </Box>

          <Chip
            label={isPackageAssigned(booking) ? 'Agent Assigned' : 'Not Assigned'}
            size="small"
            color={isPackageAssigned(booking) ? 'info' : 'warning'}
            variant={isPackageAssigned(booking) ? 'filled' : 'outlined'}
            sx={{ width: '100%', mb: 1 }}
          />

          {booking.agent_name && (
            <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" color="text.secondary">Sample Collection Agent</Typography>
              <Typography variant="body2" fontWeight={500}>{booking.agent_name}</Typography>
            </Paper>
          )}
        </CardContent>

        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth variant="contained"
            startIcon={<MedicalServices />}
            onClick={() => { setPackageAgentSearchQuery(''); setPackageAssignDialog({ open: true, booking }); }}
            disabled={isPackageAssigned(booking)}
            sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005A63' }, '&:disabled': { bgcolor: '#ccc' } }}
          >
            {isPackageAssigned(booking) ? 'Assigned' : 'Assign Sample Collection Agent'}
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  // ── Package Table View ────────────────────────────────────────────────────
  const PackageTableView = () => (
    <TableContainer component={Paper} elevation={2}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell><strong>Booking Ref</strong></TableCell>
            <TableCell><strong>Package</strong></TableCell>
            <TableCell><strong>Patient</strong></TableCell>
            <TableCell><strong>Preferred Date/Time</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Amount</strong></TableCell>
            <TableCell><strong>Assignment</strong></TableCell>
            <TableCell><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredPackageBookings.map(booking => (
            <TableRow key={booking.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{booking.booking_ref}</Typography>
                <Typography variant="caption" color="text.secondary">{booking.created_at}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{booking.package_name}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{booking.patient_name}</Typography>
                <Typography variant="caption" color="text.secondary">{booking.phone}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{booking.preferred_date}</Typography>
                <Typography variant="caption" color="text.secondary">{booking.preferred_time}</Typography>
              </TableCell>
              <TableCell>
                <Chip label={booking.status} size="small" color={getStatusColor(booking.status)} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>₹{booking.offer_price?.toFixed(2) || '0.00'}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={isPackageAssigned(booking) ? 'Assigned' : 'Not Assigned'}
                  size="small"
                  color={isPackageAssigned(booking) ? 'info' : 'warning'}
                  variant={isPackageAssigned(booking) ? 'filled' : 'outlined'}
                />
                {booking.agent_name && (
                  <Typography variant="caption" display="block" color="primary">{booking.agent_name}</Typography>
                )}
              </TableCell>
              <TableCell>
                <Button
                  size="small" variant="contained"
                  startIcon={<MedicalServices />}
                  onClick={() => { setPackageAgentSearchQuery(''); setPackageAssignDialog({ open: true, booking }); }}
                  disabled={isPackageAssigned(booking)}
                  sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005A63' }, '&:disabled': { bgcolor: '#ccc' } }}
                >
                  {isPackageAssigned(booking) ? 'Assigned' : 'Assign'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // ── Diagnostic Card View ──────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  const CardView = () => (
    <Grid container spacing={3}>
      {filteredOrders.map(order => (
        <Grid item xs={12} sm={6} md={4} key={order.id}>
          <Card sx={{
            height: '100%', display: 'flex', flexDirection: 'column',
            transition: 'all 0.3s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
          }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>{order.orderId}</Typography>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 14 }} />
                    {new Date(order.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Chip label={order.orderType.toUpperCase()} size="small" color={getOrderTypeColor(order.orderType)} sx={{ fontWeight: 500 }} />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <Person sx={{ fontSize: 16 }} /> Customer
                </Typography>
                <Typography variant="body1" fontWeight={500}>{order.customerName}</Typography>
                <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <Phone sx={{ fontSize: 14 }} /> {order.customerPhone}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Service</Typography>
                <Typography variant="body1" fontWeight={500}>{order.serviceName}</Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Status</Typography>
                  <Chip label={order.orderStatus} size="small" color={getStatusColor(order.orderStatus)} />
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary" gutterBottom>Amount</Typography>
                  <Typography variant="h6" fontWeight={600} color="primary" display="flex" alignItems="center" gap={0.5}>
                    <CurrencyRupeeIcon sx={{ fontSize: 18 }} />
                    {order.totalAmount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Box>

              {order.deliveryType === 'report' && (
                <Box mb={2}>
                  <Chip
                    icon={<Description />}
                    label={`Report Delivery (${order.reportCount || 0} report${order.reportCount !== 1 ? 's' : ''})`}
                    size="small" color="success" variant="outlined" sx={{ width: '100%' }}
                  />
                </Box>
              )}

              <Box mb={2}>
                <Chip
                  label={getAssignmentStatusLabel(order)} size="small"
                  color={getAssignmentStatusColor(order)}
                  variant={isOrderAssigned(order) ? "filled" : "outlined"}
                  sx={{ width: '100%' }}
                />
              </Box>

              {order.deliveryAgent && (
                <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    {order.orderType === 'medicine' ? 'Delivery Agent' : 'Sample Collection Agent'}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>{order.deliveryAgent.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{order.deliveryAgent.phone}</Typography>
                  <Typography variant="caption" display="block" color="primary">
                    {order.deliveryAgent.agentRole === 'delivery' ? 'Delivery' : 'Sample Collection'}
                  </Typography>
                </Paper>
              )}

              {order.reportDeliveryAgent && (
                <Paper sx={{ p: 1.5, bgcolor: '#f0f9ff', border: '1px solid #3b82f6' }}>
                  <Typography variant="caption" color="primary" gutterBottom>Report Delivery Agent</Typography>
                  <Typography variant="body2" fontWeight={500}>{order.reportDeliveryAgent.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{order.reportDeliveryAgent.phone}</Typography>
                  <Typography variant="caption" display="block" color="primary">Report Delivery</Typography>
                </Paper>
              )}
            </CardContent>

            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth variant="contained" startIcon={<LocalShipping />}
                onClick={() => { setAgentSearchQuery(''); setAssignDialog({ open: true, order }); }}
                disabled={isOrderAssigned(order)}
                sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005A63' }, '&:disabled': { bgcolor: '#ccc' } }}
              >
                {isOrderAssigned(order) ? 'Assigned' : `Assign ${getAssignmentTypeLabel(order)} Agent`}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // ── Diagnostic Table View ─────────────────────────────────────────────────
  const TableView = () => (
    <TableContainer component={Paper} elevation={2}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell><strong>Order ID</strong></TableCell>
            <TableCell><strong>Type</strong></TableCell>
            <TableCell><strong>Customer</strong></TableCell>
            <TableCell><strong>Service</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Amount</strong></TableCell>
            <TableCell><strong>Assignment Status</strong></TableCell>
            <TableCell><strong>Assigned Agent</strong></TableCell>
            <TableCell><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredOrders.map(order => (
            <TableRow key={order.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{order.orderId}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(order.createdAt).toLocaleDateString()}
                </Typography>
                {order.deliveryType === 'report' && (
                  <Chip icon={<Description />} label="Report" size="small" color="success" variant="outlined"
                    sx={{ mt: 0.5, display: 'block', width: 'fit-content' }} />
                )}
              </TableCell>
              <TableCell>
                <Chip label={order.orderType.toUpperCase()} size="small" color={getOrderTypeColor(order.orderType)} sx={{ fontWeight: 500 }} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{order.customerName}</Typography>
                <Typography variant="caption" color="text.secondary">{order.customerPhone}</Typography>
              </TableCell>
              <TableCell><Typography variant="body2">{order.serviceName}</Typography></TableCell>
              <TableCell>
                <Chip label={order.orderStatus} size="small" color={getStatusColor(order.orderStatus)} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>₹{order.totalAmount?.toFixed(2) || '0.00'}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={getAssignmentStatusLabel(order)} size="small"
                  color={getAssignmentStatusColor(order)}
                  variant={isOrderAssigned(order) ? "filled" : "outlined"}
                />
              </TableCell>
              <TableCell>
                {order.deliveryAgent && (
                  <Box mb={order.reportDeliveryAgent ? 1 : 0}>
                    <Typography variant="body2" fontWeight={500}>{order.deliveryAgent.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{order.deliveryAgent.phone}</Typography>
                    <Typography variant="caption" display="block" color="primary">
                      {order.deliveryAgent.agentRole === 'delivery' ? 'Delivery' : 'Sample Collection'}
                    </Typography>
                  </Box>
                )}
                {order.reportDeliveryAgent && (
                  <Box sx={{ bgcolor: '#f0f9ff', p: 1, borderRadius: 1, border: '1px solid #3b82f6' }}>
                    <Typography variant="caption" color="primary" fontWeight={600}>Report Delivery</Typography>
                    <Typography variant="body2" fontWeight={500}>{order.reportDeliveryAgent.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{order.reportDeliveryAgent.phone}</Typography>
                  </Box>
                )}
                {!order.deliveryAgent && !order.reportDeliveryAgent && (
                  <Typography variant="body2" color="text.secondary">-</Typography>
                )}
              </TableCell>
              <TableCell>
                <Button
                  size="small" variant="contained" startIcon={<LocalShipping />}
                  onClick={() => { setAgentSearchQuery(''); setAssignDialog({ open: true, order }); }}
                  disabled={isOrderAssigned(order)}
                  sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005A63' }, '&:disabled': { bgcolor: '#ccc' } }}
                >
                  {isOrderAssigned(order) ? 'Assigned' : 'Assign'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // ── Render ────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#006D77' }} />
      </Box>
    );
  }

  const pendingPackageCount = packageBookings.filter(b => !isPackageAssigned(b)).length;

  // ── Role-based visibility flags ───────────────────────────────────────────
  // medicineadmin  → only medicine orders, no package tab
  // diagnosticadmin → diagnostic bookings + package bookings tab
  // admin          → everything
  const canSeePackageTab  = userRole === 'admin' || userRole === 'diagnosticadmin';
  const canSeeMedicineTab = userRole === 'admin' || userRole === 'medicineadmin';

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Box sx={{ p: 3, ml: { md: '300px' } }}>

        {/* ── Page Header ── */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" sx={{ mb: 1, color: '#1e293b' }}>
            Delivery Orders Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage orders and assign delivery/sample collection agents
          </Typography>
        </Box>

        {/* ── Stats ── */}
        <Grid container spacing={2} mb={3}>
          {/* Show medicine stat only for admin / medicineadmin */}
          {canSeeMedicineTab && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="body2" color="text.secondary">Medicine Orders</Typography>
                <Typography variant="h4" fontWeight={600}>
                  {orders.filter(o => o.orderType === 'medicine').length}
                </Typography>
              </Paper>
            </Grid>
          )}
          {/* Diagnostic bookings stat for admin / diagnosticadmin */}
          {(userRole === 'admin' || userRole === 'diagnosticadmin') && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="body2" color="text.secondary">Diagnostic Bookings</Typography>
                <Typography variant="h4" fontWeight={600}>
                  {orders.filter(o => o.orderType === 'booking').length}
                </Typography>
              </Paper>
            </Grid>
          )}
          {/* Package bookings stat for admin / diagnosticadmin */}
          {canSeePackageTab && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="body2" color="text.secondary">Package Bookings</Typography>
                <Typography variant="h4" fontWeight={600} color={pendingPackageCount > 0 ? 'warning.main' : 'inherit'}>
                  {packageBookings.length}
                  {pendingPackageCount > 0 && (
                    <Typography component="span" variant="body2" color="warning.main" sx={{ ml: 1 }}>
                      ({pendingPackageCount} unassigned)
                    </Typography>
                  )}
                </Typography>
              </Paper>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary">Active Agents</Typography>
              <Typography variant="h4" fontWeight={600}>{agents.length}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ── UNIFIED ACTION BAR + CONTENT ───────────────────────────────── */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <Paper sx={{ p: 2.5, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Search — text changes based on active tab */}
          <TextField
            placeholder={filterTab === 'packages'
              ? 'Search by Ref, Patient Name, Phone, or Package...'
              : 'Search by Order ID, Customer Name, or Phone...'}
            variant="outlined" size="small"
            value={filterTab === 'packages' ? packageSearchQuery : searchQuery}
            onChange={e => filterTab === 'packages'
              ? setPackageSearchQuery(e.target.value)
              : setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ flexGrow: 1, minWidth: 250, maxWidth: 400 }}
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* ── Filter tabs ── */}
            {userRole === 'admin' && (
              <Tabs
                value={filterTab}
                onChange={(_, v) => setFilterTab(v)}
                TabIndicatorProps={{ style: { backgroundColor: '#006D77' } }}
              >
                <Tab label="All Orders" value="all" sx={{ '&.Mui-selected': { color: '#006D77' } }} />
                <Tab label="Medicine"   value="medicine" sx={{ '&.Mui-selected': { color: '#006D77' } }} />
                <Tab label="Bookings"   value="booking"  sx={{ '&.Mui-selected': { color: '#006D77' } }} />
                {canSeePackageTab && (
                  <Tab
                    value="packages"
                    sx={{ fontWeight: 600, '&.Mui-selected': { color: '#006D77' } }}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        Packages
                        {pendingPackageCount > 0 && (
                          <Chip label={pendingPackageCount} size="small" color="error"
                            sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                        )}
                      </Box>
                    }
                  />
                )}
              </Tabs>
            )}

            {/* diagnosticadmin: Bookings + Packages tabs */}
            {userRole === 'diagnosticadmin' && (
              <Tabs
                value={filterTab}
                onChange={(_, v) => setFilterTab(v)}
                TabIndicatorProps={{ style: { backgroundColor: '#006D77' } }}
              >
                <Tab label="Diagnostic Bookings" value="booking" sx={{ '&.Mui-selected': { color: '#006D77' } }} />
                <Tab
                  value="packages"
                  sx={{ fontWeight: 600, '&.Mui-selected': { color: '#006D77' } }}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      Packages
                      {pendingPackageCount > 0 && (
                        <Chip label={pendingPackageCount} size="small" color="error"
                          sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                      )}
                    </Box>
                  }
                />
              </Tabs>
            )}

            {/* medicineadmin: plain label, no tab switching */}
            {userRole === 'medicineadmin' && (
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Medicine Orders
              </Typography>
            )}

            {/* View toggle — shown for all, switches between cards/table */}
            <ToggleButtonGroup
              value={filterTab === 'packages' ? packageViewMode : viewMode}
              exclusive
              onChange={(_, m) => {
                if (!m) return;
                filterTab === 'packages' ? setPackageViewMode(m) : setViewMode(m);
              }}
              size="small"
            >
              <ToggleButton value="cards"><Tooltip title="Card View"><GridViewIcon /></Tooltip></ToggleButton>
              <ToggleButton value="table"><Tooltip title="Table View"><ViewListIcon /></Tooltip></ToggleButton>
            </ToggleButtonGroup>

            {/* Refresh — delegates to the correct fetch */}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={filterTab === 'packages' ? fetchPackageBookings : fetchData}
              disabled={filterTab === 'packages' && packageLoading}
              sx={{ borderColor: '#006D77', color: '#006D77', '&:hover': { borderColor: '#005A63', bgcolor: '#006D7710' } }}
            >
              {filterTab === 'packages' && packageLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </Box>
        </Paper>

        {/* ── Orders / Bookings content ── */}
        {filterTab !== 'packages' && (
          <>
            {filteredOrders.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searchQuery || filterTab !== 'all'
                    ? 'No orders match your search criteria'
                    : 'No orders ready for assignment'}
                </Typography>
              </Paper>
            ) : (
              viewMode === 'cards'
                ? <Grid container spacing={3}><CardView /></Grid>
                : <TableView />
            )}
          </>
        )}

        {/* ── Packages content ── */}
        {filterTab === 'packages' && (
          <>
            {packageLoading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress sx={{ color: '#006D77' }} />
              </Box>
            ) : filteredPackageBookings.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {packageSearchQuery
                    ? 'No package bookings match your search'
                    : 'No confirmed package bookings awaiting agent assignment'}
                </Typography>
              </Paper>
            ) : packageViewMode === 'cards' ? (
              <Grid container spacing={3}>
                {filteredPackageBookings.map(booking => (
                  <PackageCard key={booking.id} booking={booking} />
                ))}
              </Grid>
            ) : (
              <PackageTableView />
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ── DIALOGS ────────────────────────────────────────────────────── */}
        {/* ══════════════════════════════════════════════════════════════════ */}

        {/* ── Diagnostic: Assign Agent Dialog ── */}
        <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, order: null })} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={600}>
                Assign {assignDialog.order && getAssignmentTypeLabel(assignDialog.order)} Agent
              </Typography>
              <IconButton onClick={() => setAssignDialog({ open: false, order: null })} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            {assignDialog.order && (
              <>
                <Box mb={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Order Details</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {assignDialog.order.orderId} - {assignDialog.order.customerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{assignDialog.order.serviceName}</Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={assignDialog.order.orderType.toUpperCase()} size="small" color={getOrderTypeColor(assignDialog.order.orderType)} />
                    <Chip label={assignDialog.order.orderStatus} size="small" color={getStatusColor(assignDialog.order.orderStatus)} />
                    {(assignDialog.order.deliveryType === 'report' || assignDialog.order.orderStatus === 'report-dispatched') && (
                      <Chip icon={<Description />} label="REPORT DELIVERY" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                </Box>

                <TextField
                  fullWidth placeholder="Search by agent name, ID, or phone..."
                  variant="outlined" size="small"
                  value={agentSearchQuery} onChange={e => setAgentSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                  sx={{ mt: 2, mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select {getAssignmentTypeLabel(assignDialog.order)} Agent
                </Typography>

                {getSearchFilteredAgents(assignDialog.order).length === 0 ? (
                  <Paper sx={{ p: 3, bgcolor: '#fff3e0', border: '1px solid #ff9800', mt: 2 }}>
                    <Typography color="text.secondary">
                      {agentSearchQuery.trim()
                        ? `No agents found matching "${agentSearchQuery}"`
                        : `No available ${getAssignmentTypeLabel(assignDialog.order).toLowerCase()} agents.`}
                    </Typography>
                  </Paper>
                ) : (
                  <AgentList
                    agents={getSearchFilteredAgents(assignDialog.order)}
                    onSelect={agent => setConfirmDialog({ open: true, agent, order: assignDialog.order })}
                  />
                )}
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setAssignDialog({ open: false, order: null })} variant="outlined">Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* ── Package: Assign Agent Dialog ── */}
        <Dialog open={packageAssignDialog.open} onClose={() => setPackageAssignDialog({ open: false, booking: null })} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={600}>Assign Sample Collection Agent</Typography>
              <IconButton onClick={() => setPackageAssignDialog({ open: false, booking: null })} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            {packageAssignDialog.booking && (
              <>
                <Box mb={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Package Booking Details</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {packageAssignDialog.booking.booking_ref} — {packageAssignDialog.booking.patient_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {packageAssignDialog.booking.package_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    📅 {packageAssignDialog.booking.preferred_date} · {packageAssignDialog.booking.preferred_time}
                  </Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label="PACKAGE" size="small" color="secondary" />
                    <Chip label={packageAssignDialog.booking.status} size="small" color={getStatusColor(packageAssignDialog.booking.status)} />
                  </Box>
                </Box>

                <TextField
                  fullWidth placeholder="Search by agent name, ID, or phone..."
                  variant="outlined" size="small"
                  value={packageAgentSearchQuery} onChange={e => setPackageAgentSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select Sample Collection Agent
                </Typography>

                <AgentList
                  agents={getSearchFilteredPackageAgents()}
                  onSelect={agent => setPackageConfirmDialog({ open: true, agent, booking: packageAssignDialog.booking })}
                />
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setPackageAssignDialog({ open: false, booking: null })} variant="outlined">Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* ── Diagnostic: Confirm Assignment Dialog ── */}
        <CustomDialog
          open={confirmDialog.open}
          type="warning"
          title="Confirm Agent Assignment"
          message={`Are you sure you want to assign ${confirmDialog.agent?.name || ''} to this order?\n\nOrder ID: ${confirmDialog.order?.orderId || ''}\nCustomer: ${confirmDialog.order?.customerName || ''}\nType: ${confirmDialog.order && getAssignmentTypeLabel(confirmDialog.order)}`}
          buttonText="Yes, Assign"
          secondaryButtonText="Cancel"
          onClose={handleConfirmAssignment}
          onSecondaryAction={() => setConfirmDialog({ open: false, agent: null, order: null })}
          loading={assignLoading}
        />

        {/* ── Package: Confirm Assignment Dialog ── */}
        <CustomDialog
          open={packageConfirmDialog.open}
          type="warning"
          title="Confirm Agent Assignment"
          message={`Assign ${packageConfirmDialog.agent?.name || ''} for sample collection?\n\nBooking: ${packageConfirmDialog.booking?.booking_ref || ''}\nPatient: ${packageConfirmDialog.booking?.patient_name || ''}\nPackage: ${packageConfirmDialog.booking?.package_name || ''}`}
          buttonText="Yes, Assign"
          secondaryButtonText="Cancel"
          onClose={handlePackageConfirmAssignment}
          onSecondaryAction={() => setPackageConfirmDialog({ open: false, agent: null, booking: null })}
          loading={packageAssignLoading}
        />

        {/* ── Notification Dialog ── */}
        <CustomDialog
          open={notificationDialog.open}
          type={notificationDialog.type}
          title={notificationDialog.title}
          message={notificationDialog.message}
          buttonText="OK"
          onClose={() => setNotificationDialog({ ...notificationDialog, open: false })}
        />

      </Box>
    </Box>
  );
};

export default DeliveryOrdersManagement;